import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import {
  templatesApi,
  recordsApi,
  signaturesApi,
  fontsApi,
  competitionsApi,
  sportsApi,
  attributionsApi,
  siteSettingsApi,
} from "../../../service/certificate"
import { all_routes } from "../../router/all_routes"

const routes = all_routes

// Workflow cards (Generate is the accent/primary action; Records shows a count).
const PRIMARY_CARDS = [
  {
    to: routes.certGenerate,
    label: "Generate",
    description: "Class Result · Sports · Participation flows. Pick a template, scope, and students.",
    countKey: null as string | null,
    accent: true,
  },
  {
    to: routes.certRecords,
    label: "Records",
    description: "All generated certificates. Open the PDF, bulk-download a ZIP, or remove a mistake.",
    countKey: "records",
    accent: false,
  },
]

const READY_CARDS = [
  { to: routes.certTemplates, label: "Templates", description: "Saved certificate designs. Open one to launch the designer.", countKey: "templates" },
  { to: routes.certCompetitions, label: "Competitions", description: "Activities used for Participation certificates (Quiz, Debate, Art, …).", countKey: "competitions" },
  { to: routes.certSports, label: "Sports", description: "Master list of sports. One sport per Sports certificate.", countKey: "sports" },
  { to: routes.certSignatures, label: "Signatures", description: "Signer images (Principal, Examination Officer, …) for templates.", countKey: "signatures" },
  { to: routes.certAttributions, label: "Attributions", description: "Institutional credits — sponsors, verifying bodies, partner orgs.", countKey: "attributions" },
  { to: routes.certFonts, label: "Fonts", description: "Upload custom typefaces (.ttf · .otf · .woff · .woff2) for use in templates.", countKey: "fonts" },
]

const CertOverview = () => {
  const [stats, setStats] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    ;(async () => {
      const safe = async (p: Promise<any>) => {
        try {
          const r = await p
          return Array.isArray(r?.data) ? r.data.length : 0
        } catch {
          return 0
        }
      }
      const [records, templates, competitions, sports, signatures, attributions, fonts] = await Promise.all([
        safe(recordsApi.list()),
        safe(templatesApi.list()),
        safe(competitionsApi.list()),
        safe(sportsApi.list()),
        safe(signaturesApi.list()),
        safe(attributionsApi.list()),
        safe(fontsApi.list()),
      ])
      setStats({ records, templates, competitions, sports, signatures, attributions, fonts })
      setLoading(false)
    })()
  }, [])

  const count = (k: string | null) => (k ? (loading ? "—" : stats[k] ?? 0) : null)

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-3">
          <h3 className="page-title mb-1">Certificates</h3>
          <p className="text-muted mb-0">
            Generate and manage student certificates — class result, sports, participation.
          </p>
        </div>

        <SchoolNameEditor />

        {/* Workflow */}
        <div className="mb-4">
          <h6 className="text-uppercase text-muted fw-semibold small mb-2">Workflow</h6>
          <div className="row g-3">
            {PRIMARY_CARDS.map((card) => (
              <div className="col-md-6" key={card.to}>
                <Link
                  to={card.to}
                  className={`card h-100 text-decoration-none ${card.accent ? "bg-dark" : "text-reset"}`}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-baseline justify-content-between gap-2">
                      {/* Inline colour so the app theme's heading colour can't hide it on the dark card */}
                      <h5 className="mb-0" style={card.accent ? { color: "#fff" } : undefined}>
                        {card.label}
                      </h5>
                      {card.countKey && (
                        <span className="fs-3 fw-semibold" style={card.accent ? { color: "#fff" } : undefined}>
                          {count(card.countKey)}
                        </span>
                      )}
                    </div>
                    <p
                      className={`mb-0 mt-1 small ${card.accent ? "" : "text-muted"}`}
                      style={card.accent ? { color: "rgba(255,255,255,0.85)" } : undefined}
                    >
                      {card.description}
                    </p>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Master data */}
        <div className="mb-4">
          <h6 className="text-uppercase text-muted fw-semibold small mb-2">Master data</h6>
          <div className="row g-3">
            {READY_CARDS.map((card) => (
              <div className="col-md-6 col-lg-4" key={card.to}>
                <Link to={card.to} className="card h-100 text-decoration-none text-reset">
                  <div className="card-body">
                    <div className="d-flex align-items-baseline justify-content-between gap-2">
                      <h5 className="mb-0">{card.label}</h5>
                      <span className="fs-3 fw-semibold">{count(card.countKey)}</span>
                    </div>
                    <p className="mb-0 mt-1 small text-muted">{card.description}</p>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-4 text-muted" style={{ fontSize: 12 }}>
          Schema &amp; routing, master-data CRUD, visual designer, and the generation pipeline &amp;
          records browser are all in place — the certificate module is feature-complete.
        </p>
      </div>
    </div>
  )
}

// Global school-name fallback — resolves {{school.name}} on certificates. Wired to
// our existing certificate site-settings (full editor lives on the Settings page).
function SchoolNameEditor() {
  const [value, setValue] = useState("")
  const [others, setOthers] = useState<{ schoolCode: string; address: string; phone: string }>({
    schoolCode: "",
    address: "",
    phone: "",
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        const res = await siteSettingsApi.get()
        const d = res?.data || {}
        setValue(d.schoolName || "")
        setOthers({ schoolCode: d.schoolCode || "", address: d.address || "", phone: d.phone || "" })
      } catch {
        /* leave blank */
      }
    })()
  }, [])

  const save = async () => {
    setSaving(true)
    try {
      // Resend the other fields so the quick edit never blanks them.
      const fd = new FormData()
      fd.append("schoolName", value)
      fd.append("schoolCode", others.schoolCode)
      fd.append("address", others.address)
      fd.append("phone", others.phone)
      await siteSettingsApi.update(fd)
      toast.success("Saved")
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card mb-4">
      <div className="card-body">
        <h6 className="text-uppercase text-muted fw-semibold small mb-1">School name (global fallback)</h6>
        <p className="text-muted small mb-3">
          The global school name used when no campus-specific name is set. It resolves the{" "}
          <code>{"{{school.name}}"}</code> placeholder in the certificate Designer. Manage the full details
          (code, address, phone, logo) on the{" "}
          <Link to={routes.certSettings}>Settings</Link> page.
        </p>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <div className="flex-grow-1" style={{ minWidth: 240 }}>
            <input
              className="form-control"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="The Eon Academy"
            />
          </div>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CertOverview
